import { useEffect, useMemo, useRef } from 'react';
import { CircleGeometry } from 'three';
import type { MeshStandardMaterial } from 'three';
import { useFrame } from '@react-three/fiber';
import {
  DRIVING_SURFACE_PATCHES,
  type SurfacePatch,
} from '../model/drivingSurface';
import { sampleDrivingTerrain } from '../model/drivingTerrain';
import type { RuntimeWeather } from '../model/weatherTransition';

function createTerrainPatchGeometry(patch: SurfacePatch): CircleGeometry {
  const geometry = new CircleGeometry(1, 64);
  const positions = geometry.getAttribute('position');
  const cos = Math.cos(patch.rotation);
  const sin = Math.sin(patch.rotation);
  const yOffset = patch.type === 'puddle' ? 0.008 : 0.003;

  for (let index = 0; index < positions.count; index += 1) {
    const localX = positions.getX(index) * patch.radiusX;
    const localZ = positions.getY(index) * patch.radiusZ;
    const x = patch.x + localX * cos - localZ * sin;
    const z = patch.z + localX * sin + localZ * cos;
    const y = sampleDrivingTerrain(x, z).height + yOffset;
    positions.setXYZ(index, x, y, z);
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function SurfacePatchMesh({ patch, weather }: { patch: SurfacePatch; weather: RuntimeWeather }) {
  const geometry = useMemo(() => createTerrainPatchGeometry(patch), [patch]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  const isPuddle = patch.type === 'puddle';
  const materialRef = useRef<MeshStandardMaterial | null>(null);

  // 눈 여부는 렌더가 아니라 프레임에서 읽습니다(전환 중 재렌더 방지).
  useFrame(() => {
    const material = materialRef.current;
    if (material === null) {
      return;
    }
    material.color.set(
      isPuddle
        ? weather.snowy ? '#b9c4cb' : '#607b88'
        : weather.snowy ? '#aeb2ae' : '#766957'
    );
  });

  return (
    <mesh geometry={geometry}>
      {isPuddle ? (
        <meshStandardMaterial
          ref={materialRef}
          color="#607b88"
          roughness={0.08}
          metalness={0.2}
          envMapIntensity={1.8}
          transparent
          opacity={0.72}
          depthWrite={false}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      ) : (
        <meshStandardMaterial
          ref={materialRef}
          color="#766957"
          roughness={0.98}
          metalness={0}
          polygonOffset
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
        />
      )}
    </mesh>
  );
}

/** 물리 샘플러와 같은 타원을 같은 지형 높이로 변형해 노면 경사에 밀착시킵니다. */
export function DrivingSurfacePatches({ weather }: { weather: RuntimeWeather }) {
  return (
    <>
      {DRIVING_SURFACE_PATCHES.map((patch) => (
        <SurfacePatchMesh key={patch.id} patch={patch} weather={weather} />
      ))}
    </>
  );
}
